/**
 * Attachment Grid Component
 *
 * Displays bug report attachments as a responsive grid of thumbnails.
 * Supports click-to-preview, hover-to-remove, and type badges.
 *
 * @module AttachmentGrid
 */

import React, { useCallback, useState } from 'react';
import type { ClientAttachment } from '@wds/bug-reporter-types';
import { isImageFile, getFileExtension } from './image-utils';

/**
 * Props for AttachmentGrid component
 */
export interface AttachmentGridProps {
  /** Array of attachments to display */
  attachments: ClientAttachment[];

  /** Callback when attachment is clicked (for preview) */
  onAttachmentClick?: (attachment: ClientAttachment) => void;

  /** Callback when attachment is removed */
  onAttachmentRemove?: (attachmentId: string) => void;

  /** Maximum number of attachments allowed (for counter display) */
  maxAttachments?: number;

  /** Whether removal is disabled */
  disabled?: boolean;

  /** Class name for custom styling */
  className?: string;
}

/**
 * Attachment grid component
 *
 * Features:
 * - Responsive grid layout (1-2 columns on mobile, 3-4 on desktop)
 * - Thumbnail display with 64x64px minimum size
 * - Hover-to-remove functionality with overlay
 * - Type badges (camera icon for screenshots, upload icon for files)
 * - Attachment counter "(X/10)"
 * - Accessibility support (keyboard navigation, ARIA labels)
 * - Loading and disabled states
 *
 * @example
 * ```tsx
 * <AttachmentGrid
 *   attachments={attachments}
 *   onAttachmentClick={(attachment) => setShowPreview(attachment)}
 *   onAttachmentRemove={(id) => removeAttachment(id)}
 *   maxAttachments={10}
 * />
 * ```
 */
export const AttachmentGrid: React.FC<AttachmentGridProps> = ({
  attachments,
  onAttachmentClick,
  onAttachmentRemove,
  maxAttachments = 10,
  disabled = false,
  className = '',
}) => {
  // Track hover state for each attachment
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  /**
   * Handle attachment click
   * Opens preview modal if callback provided
   */
  const handleAttachmentClick = useCallback(
    (attachment: ClientAttachment) => {
      console.info('[AttachmentGrid] Attachment clicked:', {
        id: attachment.id,
        type: attachment.type,
      });
      onAttachmentClick?.(attachment);
    },
    [onAttachmentClick]
  );

  /**
   * Handle remove button click
   * Removes attachment if callback provided
   */
  const handleRemoveClick = useCallback(
    (event: React.MouseEvent, attachmentId: string) => {
      event.stopPropagation(); // Prevent opening preview
      console.info('[AttachmentGrid] Remove attachment:', attachmentId);
      onAttachmentRemove?.(attachmentId);
    },
    [onAttachmentRemove]
  );

  /**
   * Handle mouse enter for remove button overlay
   */
  const handleMouseEnter = useCallback((attachmentId: string) => {
    setHoveredId(attachmentId);
  }, []);

  /**
   * Handle mouse leave to hide remove button overlay
   */
  const handleMouseLeave = useCallback(() => {
    setHoveredId(null);
  }, []);

  /**
   * Get icon for attachment type
   */
  const getTypeIcon = useCallback((type: ClientAttachment['type']) => {
    const isScreenshot = type === 'screenshot';

    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          width: 12,
          height: 12,
          color: '#ffffff',
        }}
        aria-hidden="true"
      >
        {isScreenshot ? (
          // Camera icon for screenshots
          <>
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="13" r="4" />
          </>
        ) : (
          // Upload icon for file uploads
          <>
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </>
        )}
      </svg>
    );
  }, []);

  // Don't render if no attachments
  if (attachments.length === 0) {
    return null;
  }

  return (
    <div
      className={`bug-reporter-attachment-grid ${className}`.trim()}
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(64px, 1fr))',
        gap: '8px',
        marginTop: '12px',
      }}
      data-bug-reporter-attachment-grid
    >
      {/* Attachment Counter */}
      <div
        style={{
          gridColumn: '1 / -1',
          fontSize: 12,
          color: '#6b7280',
          marginBottom: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
        data-bug-reporter-attachment-counter
      >
        <span>Attachments ({attachments.length}/{maxAttachments})</span>
        <span style={{ fontSize: 11, color: '#9ca3af' }}>
          {(attachments.reduce((total, a) => total + a.size, 0) / (1024 * 1024)).toFixed(2)} MB
        </span>
      </div>

      {/* Attachment Thumbnails */}
      {attachments.map((attachment) => {
        const isHovered = hoveredId === attachment.id;

        return (
          <div
            key={attachment.id}
            className="bug-reporter-attachment-item"
            style={{
              position: 'relative',
              width: '100%',
              aspectRatio: '1',
              borderRadius: '6px',
              overflow: 'hidden',
              cursor: onAttachmentClick && !disabled ? 'pointer' : 'default',
              border: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
            }}
            onClick={() => !disabled && handleAttachmentClick(attachment)}
            onMouseEnter={() => handleMouseEnter(attachment.id)}
            onMouseLeave={handleMouseLeave}
            role={onAttachmentClick && !disabled ? 'button' : 'img'}
            tabIndex={onAttachmentClick && !disabled ? 0 : -1}
            aria-label={`Attachment ${attachment.originalName || attachment.type}`}
            data-bug-reporter-attachment-item
            data-attachment-id={attachment.id}
            data-attachment-type={attachment.type}
          >
            {/* Thumbnail - Image or File Extension */}
            {attachment.file && isImageFile(attachment.file) ? (
              <img
                src={attachment.previewUrl}
                alt={attachment.originalName || attachment.type}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
                loading="lazy"
              />
            ) : (
              /* Non-image file placeholder with extension */
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#e5e7eb',
                  color: '#374151',
                }}
              >
                {/* File icon */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ width: 24, height: 24, marginBottom: 4 }}
                  aria-hidden="true"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                {/* Extension label */}
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    maxWidth: '90%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {getFileExtension(attachment.originalName || '')}
                </span>
              </div>
            )}

            {/* Type Badge */}
            <div
              style={{
                position: 'absolute',
                top: '4px',
                left: '4px',
                width: '20px',
                height: '20px',
                borderRadius: '4px',
                backgroundColor: attachment.type === 'screenshot' ? '#3b82f6' : '#10b981',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
              }}
              data-bug-reporter-type-badge
              aria-hidden="true"
            >
              {getTypeIcon(attachment.type)}
            </div>

            {/* Remove Button Overlay (shown on hover) */}
            {!disabled && onAttachmentRemove && isHovered && (
              <div
                className="bug-reporter-attachment-remove-overlay"
                style={{
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: 'rgba(239, 68, 68, 0.9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'opacity 0.15s ease',
                }}
                data-bug-reporter-remove-overlay
                aria-hidden="true"
              >
                <button
                  type="button"
                  className="bug-reporter-attachment-remove-btn"
                  onClick={(e) => handleRemoveClick(e, attachment.id)}
                  aria-label={`Remove ${attachment.originalName || attachment.type}`}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  data-bug-reporter-remove-btn
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                      width: 24,
                      height: 24,
                    }}
                    aria-hidden="true"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            )}

            {/* Loading/Uploading Indicator (if needed) */}
            {attachment.file instanceof File && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '4px',
                  right: '4px',
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                }}
                aria-hidden="true"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ width: 10, height: 10 }}
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
